/**
 * Reading Progress Extension - WordPress API Foundation Integration
 * 
 * Modern implementation using WordPress API foundation packages:
 * - wp-hooks for action/filter system
 * - wp-dom-ready for initialization 
 * - Intersection Observer API for performance
 * 
 * @package LVBL Excellence Companion
 * @since 2.0.0 (Phase II.3)
 */

const { applyFilters, doAction, addFilter } = wp.hooks;

/**
 * Reading Progress Manager Class
 * Enhanced with WordPress API foundation and Intersection Observer
 */
class LVBLReadingProgress {
    constructor() {
        this.progressBar = null;
        this.progressFill = null;
        this.articleContent = null;
        this.isVisible = false;
        this.animationFrame = null;
        this.intersectionObserver = null;
        this.settings = {};
        
        // Bind methods for event listeners
        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.updateProgress = this.updateProgress.bind(this);
    }
    
    /**
     * Initialize reading progress with WordPress hooks
     */
    init() {
        // Allow configuration via WordPress filters
        this.settings = applyFilters('lvbl.readingProgress.settings', {
            color: '#007cba',
            height: 4,
            position: 'top',
            smoothScroll: true,
            showOnMobile: true,
            threshold: 100, // Show after scrolling 100px
            autoHide: true,
            contentSelector: '.post-content, .entry-content, .content, article',
            ...window.lvblReadingProgress?.settings
        });
        
        // Fire init action for extensibility
        doAction('lvbl.readingProgress.beforeInit', this.settings);
        
        this.setupIntersectionObserver();
        this.createProgressBar();
        this.bindEvents();
        this.setupContentDetection();
        
        // Fire ready action
        doAction('lvbl.readingProgress.ready', this);
    }
    
    /**
     * Setup Intersection Observer for performance optimization
     */
    setupIntersectionObserver() {
        if (!('IntersectionObserver' in window)) {
            // Fallback for older browsers
            this.useFallbackScrollDetection = true;
            return;
        }
        
        // Create observer for visibility detection
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.target === document.body) {
                        this.handleVisibilityChange(entry.isIntersecting);
                    }
                });
            },
            {
                root: null,
                rootMargin: '-100px 0px',
                threshold: 0
            }
        );
    }
    
    /**
     * Create or detect existing progress bar
     */
    createProgressBar() {
        // First check if progress bar already exists (created by PHP)
        const existingBar = document.getElementById('reading-progress-bar');
        
        if (existingBar) {
            // Use existing progress bar from PHP
            this.progressBar = existingBar;
            
            // Find or create the fill element
            this.progressFill = this.progressBar.querySelector('.reading-progress-fill');
            if (!this.progressFill) {
                this.progressFill = document.createElement('div');
                this.progressFill.className = 'reading-progress-fill';
                this.progressBar.appendChild(this.progressFill);
            }
            
            // Update the existing bar with enhanced classes for JavaScript functionality
            if (!this.progressBar.classList.contains('lvbl-reading-progress-bar')) {
                this.progressBar.classList.add('lvbl-reading-progress-bar');
            }
            
            // Add styles for JavaScript-enhanced functionality
            this.injectStyles();
            
            return;
        }
        
        // If no existing bar, create a new one (fallback)
        // Apply filters to allow customization
        const progressBarConfig = applyFilters('lvbl.readingProgress.barConfig', {
            id: 'lvbl-reading-progress',
            className: 'lvbl-reading-progress-bar reading-progress-bar',
            role: 'progressbar',
            'aria-label': 'Reading progress',
            'aria-valuemin': '0',
            'aria-valuemax': '100',
            'aria-valuenow': '0'
        });
        
        // Create container
        this.progressBar = document.createElement('div');
        Object.keys(progressBarConfig).forEach(key => {
            if (key === 'className') {
                this.progressBar.className = progressBarConfig[key];
            } else {
                this.progressBar.setAttribute(key === 'id' ? 'id' : `${key}`, progressBarConfig[key]);
            }
        });
        
        // Create fill element
        this.progressFill = document.createElement('div');
        this.progressFill.className = 'lvbl-reading-progress-fill reading-progress-fill';
        
        // Assemble elements
        this.progressBar.appendChild(this.progressFill);
        
        // Insert into DOM with positioning
        this.insertProgressBar();
        
        // Add styles
        this.injectStyles();
    }
    
    /**
     * Insert progress bar into DOM based on position setting
     */
    insertProgressBar() {
        if (this.settings.position === 'bottom') {
            document.body.appendChild(this.progressBar);
        } else {
            // Default to top
            document.body.insertBefore(this.progressBar, document.body.firstChild);
        }
    }
    
    /**
     * Setup content detection for accurate progress calculation
     */
    setupContentDetection() {
        // Find main content area using multiple selectors
        const contentSelectors = this.settings.contentSelector.split(',').map(s => s.trim());
        
        for (const selector of contentSelectors) {
            this.articleContent = document.querySelector(selector);
            if (this.articleContent) {
                break;
            }
        }
        
        // Fallback to body if no content area found
        if (!this.articleContent) {
            this.articleContent = document.body;
        }
        
        // Allow filtering of content element
        this.articleContent = applyFilters('lvbl.readingProgress.contentElement', this.articleContent);
    }
    
    /**
     * Bind event listeners with performance optimizations
     */
    bindEvents() {
        if (this.useFallbackScrollDetection) {
            // Fallback scroll detection for older browsers
            window.addEventListener('scroll', this.handleScroll, { passive: true });
        } else {
            // Modern approach with Intersection Observer
            this.intersectionObserver.observe(document.body);
            window.addEventListener('scroll', this.handleScroll, { passive: true });
        }
        
        window.addEventListener('resize', this.handleResize, { passive: true });
        
        // WordPress-specific events
        document.addEventListener('lvbl.contentUpdated', this.handleContentUpdate.bind(this));
    }
    
    /**
     * Handle scroll events with throttling
     */
    handleScroll() {
        if (this.animationFrame) {
            return;
        }
        
        this.animationFrame = requestAnimationFrame(() => {
            this.updateProgress();
            this.updateVisibility();
            this.animationFrame = null;
        });
    }
    
    /**
     * Handle resize events
     */
    handleResize() {
        // Recalculate content dimensions
        this.setupContentDetection();
        this.updateProgress();
    }
    
    /**
     * Update progress bar fill
     *
     * When a content element is detected (e.g. .entry-content), progress is
     * calculated relative to the article's position on the page so that 0 %
     * maps to the article top reaching the viewport and 100 % maps to the
     * article bottom reaching the viewport bottom.  When no specific content
     * element is found the full document scroll distance is used – matching
     * the back-to-top progress indicator.
     */
    updateProgress() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;

        let progressPercent  = 0;
        let contentHeight    = 0;
        let scrollableHeight = 0;

        if (this.articleContent && this.articleContent !== document.body) {
            // Content-aware progress: account for article's offset on the page.
            const rect          = this.articleContent.getBoundingClientRect();
            const contentTop    = rect.top + scrollTop;
            contentHeight       = this.articleContent.offsetHeight;
            const contentBottom = contentTop + contentHeight;

            // readStart  = scrollTop when article top enters the viewport top
            // readEnd    = scrollTop when article bottom reaches viewport bottom
            const readStart = contentTop;
            const readEnd   = contentBottom - windowHeight;
            scrollableHeight = readEnd - readStart;

            if (scrollableHeight > 0) {
                progressPercent = Math.min(
                    Math.max(((scrollTop - readStart) / scrollableHeight) * 100, 0),
                    100
                );
            } else if (scrollTop >= contentTop) {
                // Article fits entirely within one viewport.
                progressPercent = 100;
            }
        } else {
            // Full-document fallback (matches back-to-top indicator).
            contentHeight    = this.getContentHeight();
            scrollableHeight = contentHeight - windowHeight;

            if (scrollableHeight > 0) {
                progressPercent = Math.min((scrollTop / scrollableHeight) * 100, 100);
            }
        }
        
        // Apply filters for custom progress calculation
        progressPercent = applyFilters('lvbl.readingProgress.progressPercent', progressPercent, {
            scrollTop,
            windowHeight,
            contentHeight,
            scrollableHeight
        });
        
        // Update progress bar
        this.progressFill.style.width = `${progressPercent}%`;
        this.progressBar.setAttribute('aria-valuenow', Math.round(progressPercent));
        
        // Fire progress update action
        doAction('lvbl.readingProgress.progressUpdate', progressPercent);
    }
    
    /**
     * Get content height for progress calculation
     */
    getContentHeight() {
        if (this.articleContent) {
            return Math.max(
                this.articleContent.scrollHeight,
                this.articleContent.offsetHeight
            );
        }
        
        return Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
        );
    }
    
    /**
     * Update visibility based on scroll position
     */
    updateVisibility() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const shouldShow = scrollTop > this.settings.threshold;
        
        if (shouldShow && !this.isVisible) {
            this.show();
        } else if (!shouldShow && this.isVisible && this.settings.autoHide) {
            this.hide();
        }
    }
    
    /**
     * Show progress bar with animation
     */
    show() {
        this.isVisible = true;
        this.progressBar.classList.add('visible');
        doAction('lvbl.readingProgress.show');
    }
    
    /**
     * Hide progress bar with animation
     */
    hide() {
        this.isVisible = false;
        this.progressBar.classList.remove('visible');
        doAction('lvbl.readingProgress.hide');
    }
    
    /**
     * Handle content updates (for dynamic content)
     */
    handleContentUpdate() {
        this.setupContentDetection();
        this.updateProgress();
    }
    
    /**
     * Handle visibility changes from Intersection Observer
     */
    handleVisibilityChange(isIntersecting) {
        // This can be used for more sophisticated visibility logic
        doAction('lvbl.readingProgress.visibilityChange', isIntersecting);
    }
    
    /**
     * Inject styles while respecting theme customizer CSS variables
     */
    injectStyles() {
        const styleId = 'lvbl-reading-progress-styles';
        if (document.getElementById(styleId)) {
            return;
        }
        
        // Check if theme has set CSS custom properties (customizer integration)
        const rootStyles = getComputedStyle(document.documentElement);
        const themeProgressColor = rootStyles.getPropertyValue('--progress-color').trim();
        const themeProgressBg = rootStyles.getPropertyValue('--progress-bg').trim();
        const themeProgressHeight = rootStyles.getPropertyValue('--progress-height').trim();
        
        // Use theme colors if available, otherwise fall back to settings
        const progressColor = themeProgressColor || this.settings.color;
        const progressBg = themeProgressBg || 'rgba(0, 0, 0, 0.1)';
        const progressHeight = themeProgressHeight || `${this.settings.height}px`;
        
        // Only add enhanced JavaScript-specific styles, avoid overriding theme customizer styles
        const cssText = `
            /* Enhanced JavaScript functionality styles */
            .lvbl-reading-progress-bar:not(.reading-progress-bar) {
                position: fixed;
                top: ${this.settings.position === 'bottom' ? 'auto' : '0'};
                bottom: ${this.settings.position === 'bottom' ? '0' : 'auto'};
                left: 0;
                right: 0;
                height: ${progressHeight};
                background-color: ${progressBg};
                z-index: 9999;
                opacity: 0;
                transform: ${this.settings.position === 'bottom' ? 'translateY(100%)' : 'translateY(-100%)'};
                transition: opacity 0.3s ease, transform 0.3s ease;
            }
            
            .lvbl-reading-progress-bar.visible {
                opacity: 1;
                transform: translateY(0);
            }
            
            /* Only style fill if no theme styles exist */
            .lvbl-reading-progress-fill:not(.reading-progress-bar .reading-progress-fill) {
                height: 100%;
                background: linear-gradient(90deg, ${progressColor} 0%, ${this.adjustColor(progressColor, 20)} 100%);
                width: 0%;
                transition: width 0.1s ease-out;
            }
            
            /* Ensure theme-styled progress bars work with JavaScript enhancements */
            .reading-progress-bar.lvbl-reading-progress-bar {
                /* Inherit positioning from theme but ensure proper z-index for functionality */
                z-index: 999999 !important;
            }
            
            .reading-progress-bar .reading-progress-fill {
                /* Respect theme colors, only enhance transition */
                transition: width 0.1s ease-out;
            }
            
            @media (prefers-reduced-motion: reduce) {
                .lvbl-reading-progress-bar,
                .lvbl-reading-progress-fill,
                .reading-progress-bar .reading-progress-fill {
                    transition: none;
                }
            }
            
            @media (max-width: 768px) {
                .lvbl-reading-progress-bar:not(.reading-progress-bar) {
                    height: ${Math.max(2, parseInt(progressHeight) - 1)}px;
                }
            }
        `;
        
        // Apply style filters
        const finalCSS = applyFilters('lvbl.readingProgress.styles', cssText, this.settings);
        
        // Create and inject style element
        const styleElement = document.createElement('style');
        styleElement.id = styleId;
        styleElement.textContent = finalCSS;
        document.head.appendChild(styleElement);
    }
    
    /**
     * Adjust color brightness for gradient effect
     */
    adjustColor(color, amount) {
        // Simple color adjustment for gradient
        const usePound = color[0] === '#';
        const col = usePound ? color.slice(1) : color;
        
        if (col.length === 3) {
            // Convert short hex to long
            const expandedCol = col.split('').map(c => c + c).join('');
            return '#' + expandedCol;
        }
        
        if (col.length !== 6) {
            return color; // Return original if not valid hex
        }
        
        const num = parseInt(col, 16);
        const r = Math.min(255, Math.max(0, (num >> 16) + amount));
        const g = Math.min(255, Math.max(0, (num >> 8 & 0x00FF) + amount));
        const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
        
        return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
    }
    
    /**
     * Destroy instance and clean up
     */
    destroy() {
        // Remove event listeners
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        
        // Disconnect intersection observer
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
        
        // Cancel animation frame
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
        
        // Remove DOM elements
        if (this.progressBar && this.progressBar.parentNode) {
            this.progressBar.parentNode.removeChild(this.progressBar);
        }
        
        // Remove styles
        const styleElement = document.getElementById('lvbl-reading-progress-styles');
        if (styleElement) {
            styleElement.remove();
        }
        
        // Fire destroy action
        doAction('lvbl.readingProgress.destroy');
    }
}

/**
 * Initialize Reading Progress when DOM is ready
 * Using WordPress API foundation
 */
wp.domReady(() => {
    // Only initialize on single posts and pages by default
    const shouldInit = applyFilters('lvbl.readingProgress.shouldInit', () => {
        return document.body.classList.contains('single') || 
               document.body.classList.contains('page') ||
               document.querySelector('.post-content, .entry-content, article');
    });
    
    if (shouldInit()) {
        const readingProgress = new LVBLReadingProgress();
        readingProgress.init();
        
        // Make globally available for debugging and extensions
        window.lvblReadingProgressInstance = readingProgress;
        
        // Fire global ready action
        doAction('lvbl.readingProgress.globalReady', readingProgress);
    }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = LVBLReadingProgress;
}
